import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { type Order } from "@fitness-app/data-models/entities/Order";

import { supabase } from "~/store/initializeStore";
import { INVOICE_ERROR_FILTER, type InvoiceFilterValue } from "./getTableChangeAction";

const ITEMS_PER_PAGE = 25;

export { INVOICE_ERROR_FILTER, type InvoiceFilterValue };

export type OrderWithProductName = Order & {
  productInfo: { name: string };
  /** Selected by JSON path — see the query below; strings, as PostgREST returns them. */
  selectedPaymentMethod: string | null;
  tpayInstallmentsChannelId: string | null;
  paidAt: string | null;
  nextPaymentAttemptAt: string | null;
  blikDeclineMessage: string | null;
};

export const useProductsTransactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRange, setSelectedRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("searchTerm") || "");
  const [currentPage, setCurrentPage] = useState(
    searchParams.get("currentPage") ? Number(searchParams.get("currentPage")) : 1,
  );
  const queryClient = useQueryClient();
  const [filteredProducts, setFilteredProducts] = useState<string[]>(
    searchParams.get("products") ? searchParams.get("products")?.split(",") || [] : [],
  );
  const [filteredStatuses, setFilteredStatuses] = useState<Order["status"][]>(
    searchParams.get("status") ? (searchParams.get("status")?.split(",") as Order["status"][]) || [] : [],
  );
  const [filteredInvoice, setFilteredInvoice] = useState<InvoiceFilterValue | null>(
    searchParams.get("invoice")
      ? searchParams.get("invoice") === "true"
        ? true
        : searchParams.get("invoice") === "false"
          ? false
          : (searchParams.get("invoice") as typeof INVOICE_ERROR_FILTER)
      : null,
  );

  useEffect(() => {
    setSearchParams((prev) => ({
      ...prev,
      tab: "productsTransactions",
      currentPage,
      searchTerm,
      status: filteredStatuses ? filteredStatuses.join(",") : [],
      products: filteredProducts ? filteredProducts.join(",") : [],
      invoice: filteredInvoice !== null ? String(filteredInvoice) : "",
    }));
  }, [currentPage, filteredProducts, filteredStatuses, searchTerm, filteredInvoice]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["productsTransactions", currentPage, searchTerm, filteredProducts, filteredStatuses, filteredInvoice],
    keepPreviousData: true,
    queryFn: async () => {
      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      const stop = currentPage * ITEMS_PER_PAGE - 1;

      let query = supabase
        .from("product_order")
        .select(
          "id, client, clientEmail,createdAt,updatedAt,status,paymentGateway, installments, paymentMethodType,paymentType,crossSell,price,product,productInfo:product(name),connectedInvoice,customerId,selectedPaymentMethod:metadata->>selectedPaymentMethod,tpayInstallmentsChannelId:metadata->>tpayInstallmentsChannelId,paidAt:metadata->>paidAt,nextPaymentAttemptAt:metadata->>nextPaymentAttemptAt,blikDeclineMessage:metadata->>blikDeclineMessage",
          { count: "exact" },
        )
        .order("createdAt", { ascending: false });

      if (filteredStatuses?.length) {
        query = query.in("status", filteredStatuses);
      }

      if (filteredProducts?.length) {
        query = query.in("product", filteredProducts);
      }

      if (searchTerm) {
        query = query.or(`clientEmail.ilike.%${searchTerm}%,client->>lastName.ilike.%${searchTerm}%`);
      }

      if (filteredInvoice !== null) {
        if (filteredInvoice === true) {
          // Has invoice without error
          query = query.not("connectedInvoice", "is", null).is("connectedInvoice->errorMessage", null);
        } else if (filteredInvoice === false) {
          // No invoice
          query = query.is("connectedInvoice", null);
        } else if (filteredInvoice === INVOICE_ERROR_FILTER) {
          // Has invoice with error
          query = query.not("connectedInvoice->errorMessage", "is", null);
        }
      }

      const { error, data, count } = await query
        .range(start, stop)
        .limit(ITEMS_PER_PAGE)
        .returns<OrderWithProductName[]>();

      if (error) {
        throw new Error(error.message);
      }
      return {
        // The query projects individual JSON paths rather than the whole
        // metadata bag (it carries the entire checkout session), so `metadata`
        // arrives undefined and anything reading it renders blank. Rebuild the
        // few keys the table shows, so the cells shared with the other order
        // tables keep working unchanged.
        list: data.map((order) => ({
          ...order,
          metadata: {
            ...order.metadata,
            paidAt: order.paidAt,
            nextPaymentAttemptAt: order.nextPaymentAttemptAt,
            blikDeclineMessage: order.blikDeclineMessage,
          },
        })),
        count,
      };
    },
  });

  const clearSearch = () => {
    void queryClient.invalidateQueries(["productsTransactions"]);

    setSearchTerm("");
    setCurrentPage(1);
  };

  return {
    selectedRange,
    setSelectedRange,
    searchTerm,
    setSearchTerm,
    data,
    clearSearch,
    isFetching,
    isLoading,
    currentPage,
    pageSize: ITEMS_PER_PAGE,
    pageSizeOptions: ["50"],
    showSizeChanger: false,
    setFilteredStatuses,
    setFilteredProducts,
    setFilteredInvoice,
    setCurrentPage,
    filteredProducts,
    filteredStatuses,
    filteredInvoice,
  };
};
